import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { matchesAPI, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Play, Pause, RotateCcw, ArrowLeft, Shield, Trash2,
  Goal, ShieldCheck, Square, Trophy, Flag,
} from 'lucide-react';
import { EVENT_TYPES } from '@/components/MatchEventsManager';
import { getInitials, cn } from '@/lib/utils';
import { createAlarm, ensureAlarmUnlocked } from '@/lib/alarmSound';

const ALARM_SECONDS = 7;

const GK_ROTATION_SECONDS = 5 * 60; // 5 min
const LS_KEY_GAME = (id) => `lh_live_game_${id}`;
const LS_KEY_GK   = (id) => `lh_live_gk_${id}`;

const sortByJoinedAt = (arr) =>
  [...arr].sort((a, b) => new Date(a.joined_at) - new Date(b.joined_at));

// Quick-action grid: i 4 event types che si usano in partita dal vivo.
const QUICK_EVENTS = ['goal', 'assist', 'yellow_card', 'red_card'];

export const MatchLivePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [match, setMatch] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [userMembership, setUserMembership] = useState(null);

  // dialog scelta event (quando non è "goal" rapido)
  const [pickAction, setPickAction] = useState(null); // {team:'A'|'B', user_id} oppure null

  // dialog risultato finale
  const [showFinish, setShowFinish] = useState(false);
  const [finalA, setFinalA] = useState(0);
  const [finalB, setFinalB] = useState(0);

  const fetchMatch = async () => {
    try {
      const res = await matchesAPI.getById(id);
      setMatch(res.data.match);
      setParticipants(res.data.participants || []);
      setUserMembership(res.data.user_membership || null);
    } catch (e) {
      toast.error('Errore caricamento partita');
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await api.get(`/matches/${id}/events`);
      setEvents(res.data.events || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    Promise.all([fetchMatch(), fetchEvents()]).finally(() => setLoading(false));
    // Refresh periodico leggero per riflettere risultato/eventi aggiornati da altri device.
    const t = setInterval(() => { fetchMatch(); fetchEvents(); }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Permessi: creatore partita, organizer/admin lega, o admin piattaforma
  const isAdmin = useMemo(() => {
    if (!match || !user) return false;
    if (user.role === 'admin') return true;
    if (match.created_by === user.user_id) return true;
    return userMembership?.role === 'admin' || userMembership?.role === 'manager';
  }, [match, user, userMembership]);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Caricamento…</div>;
  }
  if (!match) {
    return <div className="p-8 text-center text-muted-foreground">Partita non trovata</div>;
  }

  const titulars = participants.filter((p) => p.status === 'confirmed');
  const teamA = sortByJoinedAt(titulars.filter((p) => p.team === 'A'));
  const teamB = sortByJoinedAt(titulars.filter((p) => p.team === 'B'));

  const scoreA = match.result?.team_a_score ?? 0;
  const scoreB = match.result?.team_b_score ?? 0;

  const recordEvent = async (event_type, team, player_id, secondary_player_id = null) => {
    setBusy(true);
    try {
      await api.post(`/matches/${id}/events`, {
        event_type, team, player_id, secondary_player_id, minute: gameMinute(),
      });
      // backend auto-incrementa il risultato per gol/penalty/own_goal
      await Promise.all([fetchEvents(), fetchMatch()]);
      // vibrazione su mobile
      if (event_type === 'goal' && window.navigator?.vibrate) window.navigator.vibrate([100, 50, 100]);
      toast.success(EVENT_TYPES[event_type]?.label || 'Evento registrato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore evento');
    } finally {
      setBusy(false);
      setPickAction(null);
    }
  };

  const deleteEvent = async (event_id) => {
    if (!window.confirm('Eliminare questo evento? Il risultato verrà aggiornato di conseguenza.')) return;
    setBusy(true);
    try {
      await api.delete(`/matches/${id}/events/${event_id}`);
      await Promise.all([fetchEvents(), fetchMatch()]);
    } catch (e) {
      toast.error('Errore eliminazione');
    } finally { setBusy(false); }
  };

  const openFinishDialog = () => {
    setFinalA(scoreA);
    setFinalB(scoreB);
    setShowFinish(true);
  };

  const finishMatch = async () => {
    setBusy(true);
    try {
      await matchesAPI.update(id, {
        result: { team_a_score: Number(finalA), team_b_score: Number(finalB) },
        status: 'completed',
      });
      toast.success('Partita conclusa');
      navigate(`/matches/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore chiusura partita');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-muted/30 pb-32">
      {/* Header sticky */}
      <div className="safe-content-sticky sticky z-20 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-3 py-2 flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            onClick={() => navigate(`/matches/${id}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-white/60">Modalità LIVE</p>
            <p className="text-sm font-semibold truncate">{match.title}</p>
          </div>
          {match.status === 'completed' && (
            <Badge variant="outline" className="bg-emerald-500/20 text-emerald-300 border-emerald-400/40">
              Completata
            </Badge>
          )}
        </div>

        {/* Score board */}
        <div className="max-w-3xl mx-auto px-3 pb-4 grid grid-cols-3 items-center gap-2">
          <TeamScoreBlock label="Team A" score={scoreA} />
          <div className="text-center">
            <GameTimer matchId={id} disabled={!isAdmin} />
          </div>
          <TeamScoreBlock label="Team B" score={scoreB} align="right" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 py-4 space-y-4">
        {!isAdmin && (
          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground text-center">
              Sei in modalità sola lettura. Solo organizer e admin possono registrare eventi.
            </CardContent>
          </Card>
        )}

        {/* GK rotation */}
        <GkRotationCard matchId={id} teamA={teamA} teamB={teamB} disabled={!isAdmin} />

        {/* Team A */}
        <TeamPanel
          label="Team A"
          team="A"
          players={teamA}
          disabled={!isAdmin || busy}
          onQuickEvent={(player_id) => recordEvent('goal', 'A', player_id)}
          onMoreEvents={(player_id) => setPickAction({ team: 'A', user_id: player_id })}
          events={events}
        />

        {/* Team B */}
        <TeamPanel
          label="Team B"
          team="B"
          players={teamB}
          disabled={!isAdmin || busy}
          onQuickEvent={(player_id) => recordEvent('goal', 'B', player_id)}
          onMoreEvents={(player_id) => setPickAction({ team: 'B', user_id: player_id })}
          events={events}
        />

        {/* Recent events */}
        <EventsFeed events={events} participants={participants} onDelete={deleteEvent} isAdmin={isAdmin} />
      </div>

      {/* Sticky bottom bar */}
      {isAdmin && match.status !== 'completed' && (
        <div className="safe-live-actions fixed bg-card border-t shadow-lg p-3 z-30">
          <div className="max-w-3xl mx-auto flex gap-2">
            <Button asChild variant="outline" className="flex-1">
              <Link to={`/matches/${id}`}><Shield className="h-4 w-4 mr-1" />Dettagli</Link>
            </Button>
            <Button onClick={openFinishDialog} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              <Flag className="h-4 w-4 mr-1" />
              Termina partita
            </Button>
          </div>
        </div>
      )}

      {/* Pick action dialog */}
      <Dialog open={!!pickAction} onOpenChange={(o) => !o && setPickAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Tipo di evento</DialogTitle>
            <DialogDescription>
              Per {participants.find((p) => p.user_id === pickAction?.user_id)?.user?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(EVENT_TYPES).map(([key, cfg]) => (
              <Button
                key={key}
                variant="outline"
                disabled={busy}
                onClick={() => recordEvent(key, pickAction.team, pickAction.user_id)}
                className="h-auto py-3 flex-col gap-1"
              >
                <span className="text-2xl">{cfg.emoji}</span>
                <span className="text-xs">{cfg.label}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Finish dialog */}
      <Dialog open={showFinish} onOpenChange={setShowFinish}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Conferma risultato finale
            </DialogTitle>
            <DialogDescription>
              Il risultato è stato aggiornato automaticamente dai gol registrati. Correggi se necessario.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Team A</Label>
              <Input
                type="number"
                min="0"
                value={finalA}
                onChange={(e) => setFinalA(e.target.value)}
                className="text-2xl font-bold text-center h-14"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Team B</Label>
              <Input
                type="number"
                min="0"
                value={finalB}
                onChange={(e) => setFinalB(e.target.value)}
                className="text-2xl font-bold text-center h-14"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinish(false)}>Annulla</Button>
            <Button onClick={finishMatch} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
              Conferma e chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ───── Components helpers ──────────────────────────────────────────────────────

const TeamScoreBlock = ({ label, score, align = 'left' }) => (
  <div className={cn('flex flex-col', align === 'right' ? 'items-end' : 'items-start')}>
    <span className="text-[10px] uppercase tracking-widest text-white/60">{label}</span>
    <span className="text-5xl font-black tabular-nums">{score}</span>
  </div>
);

// Cronometro tempo di gioco, persiste in localStorage
let _gameStartTs = null; // shared for gameMinute() helper
const GameTimer = ({ matchId, disabled }) => {
  const [running, setRunning] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startedAtRef = useRef(null);

  // Read from LS on mount
  useEffect(() => {
    const raw = window.localStorage.getItem(LS_KEY_GAME(matchId));
    if (raw) {
      try {
        const s = JSON.parse(raw);
        startedAtRef.current = s.startedAt || null;
        setElapsedSec(s.elapsedSec || 0);
        setRunning(!!s.running);
      } catch (_) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // Tick
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      const baseElapsed = elapsedSec;
      const sinceStart = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0;
      _gameStartTs = startedAtRef.current ? startedAtRef.current - baseElapsed * 1000 : null;
      // We don't setElapsed here in tight loop — use derived displayed below
      setDisplaySec(baseElapsed + sinceStart);
    }, 500);
    return () => clearInterval(t);
  }, [running, elapsedSec]);

  const [displaySec, setDisplaySec] = useState(elapsedSec);
  useEffect(() => { setDisplaySec(elapsedSec); }, [elapsedSec]);

  const persist = (state) => {
    window.localStorage.setItem(LS_KEY_GAME(matchId), JSON.stringify(state));
  };

  const handleStart = () => {
    startedAtRef.current = Date.now();
    setRunning(true);
    persist({ running: true, elapsedSec, startedAt: startedAtRef.current });
  };
  const handlePause = () => {
    const sinceStart = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0;
    const newElapsed = elapsedSec + sinceStart;
    setElapsedSec(newElapsed);
    setDisplaySec(newElapsed);
    setRunning(false);
    startedAtRef.current = null;
    persist({ running: false, elapsedSec: newElapsed, startedAt: null });
  };
  const handleReset = () => {
    setElapsedSec(0);
    setDisplaySec(0);
    setRunning(false);
    startedAtRef.current = null;
    persist({ running: false, elapsedSec: 0, startedAt: null });
  };

  const mm = String(Math.floor(displaySec / 60)).padStart(2, '0');
  const ss = String(displaySec % 60).padStart(2, '0');

  return (
    <div className="flex flex-col items-center">
      <span className="text-xs text-white/60">Tempo</span>
      <span className="text-3xl font-mono font-bold tabular-nums tracking-wider">
        {mm}:{ss}
      </span>
      <div className="flex gap-1 mt-1">
        {running ? (
          <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-white/10" onClick={handlePause} disabled={disabled}>
            <Pause className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-white/10" onClick={handleStart} disabled={disabled}>
            <Play className="h-4 w-4" />
          </Button>
        )}
        <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-white/10" onClick={handleReset} disabled={disabled}>
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Helper per allegare minute agli eventi (best-effort: legge da LS)
function gameMinute() {
  try {
    // Trova il match attualmente live, è quello dell'url
    const pathMatch = window.location.pathname.match(/\/matches\/([^/]+)\/live/);
    if (!pathMatch) return null;
    const matchId = pathMatch[1];
    const raw = window.localStorage.getItem(LS_KEY_GAME(matchId));
    if (!raw) return null;
    const s = JSON.parse(raw);
    let sec = s.elapsedSec || 0;
    if (s.running && s.startedAt) sec += Math.floor((Date.now() - s.startedAt) / 1000);
    return Math.floor(sec / 60);
  } catch (_) { return null; }
}

const GkRotationCard = ({ matchId, teamA, teamB, disabled }) => {
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(GK_ROTATION_SECONDS);
  const [keeperIndex, setKeeperIndex] = useState(0);
  const [alarmActive, setAlarmActive] = useState(false);
  const startedAtRef = useRef(null);
  const alarmRef = useRef(null);

  // Lazy init dell'allarme
  if (!alarmRef.current) alarmRef.current = createAlarm();

  useEffect(() => {
    const raw = window.localStorage.getItem(LS_KEY_GK(matchId));
    if (raw) {
      try {
        const s = JSON.parse(raw);
        setRunning(!!s.running);
        setRemaining(s.remaining ?? GK_ROTATION_SECONDS);
        setKeeperIndex(s.keeperIndex ?? 0);
        startedAtRef.current = s.startedAt || null;
      } catch (_) { /* ignore */ }
    }
  }, [matchId]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      const passed = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0;
      const left = Math.max(0, (remaining || 0) - passed);
      setDisplayLeft(left);
      if (left === 0) {
        rotate();
      }
    }, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, remaining]);

  const [displayLeft, setDisplayLeft] = useState(remaining);
  useEffect(() => { setDisplayLeft(remaining); }, [remaining]);

  const persist = (state) => {
    window.localStorage.setItem(LS_KEY_GK(matchId), JSON.stringify(state));
  };

  const triggerAlarm = () => {
    setAlarmActive(true);
    try { alarmRef.current?.play(ALARM_SECONDS); } catch (_) { /* ignore */ }
    setTimeout(() => setAlarmActive(false), ALARM_SECONDS * 1000);
  };

  const stopAlarm = () => {
    try { alarmRef.current?.stop(); } catch (_) { /* ignore */ }
    setAlarmActive(false);
  };

  const rotate = () => {
    setKeeperIndex((i) => i + 1);
    setRemaining(GK_ROTATION_SECONDS);
    setDisplayLeft(GK_ROTATION_SECONDS);
    startedAtRef.current = Date.now();
    persist({ running: true, remaining: GK_ROTATION_SECONDS, keeperIndex: keeperIndex + 1, startedAt: startedAtRef.current });
    triggerAlarm();
  };

  const handleStart = async () => {
    // Sblocca audio sotto user-gesture (necessario su iOS Safari)
    await ensureAlarmUnlocked();
    startedAtRef.current = Date.now();
    setRunning(true);
    persist({ running: true, remaining, keeperIndex, startedAt: startedAtRef.current });
  };
  const handlePause = () => {
    const passed = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0;
    const newRem = Math.max(0, remaining - passed);
    setRemaining(newRem); setDisplayLeft(newRem);
    setRunning(false);
    startedAtRef.current = null;
    persist({ running: false, remaining: newRem, keeperIndex, startedAt: null });
  };
  const handleReset = () => {
    setRunning(false); setRemaining(GK_ROTATION_SECONDS); setDisplayLeft(GK_ROTATION_SECONDS);
    setKeeperIndex(0); startedAtRef.current = null;
    persist({ running: false, remaining: GK_ROTATION_SECONDS, keeperIndex: 0, startedAt: null });
  };
  const handleSkip = () => rotate();

  const mm = String(Math.floor(displayLeft / 60)).padStart(2, '0');
  const ss = String(displayLeft % 60).padStart(2, '0');

  const kpA = teamA[keeperIndex % Math.max(teamA.length, 1)];
  const kpB = teamB[keeperIndex % Math.max(teamB.length, 1)];

  // Cleanup all'unmount
  useEffect(() => () => alarmRef.current?.stop?.(), []);

  return (
    <>
      {alarmActive && (
        <button
          type="button"
          onClick={stopAlarm}
          className="safe-screen-content fixed inset-0 z-[60] flex flex-col items-center justify-center bg-rose-600/95 text-white animate-pulse cursor-pointer select-none"
          aria-label="Cambio portiere — tocca per fermare l'allarme"
        >
          <ShieldCheck className="h-24 w-24 mb-4" strokeWidth={2.5} />
          <p className="text-4xl font-black uppercase tracking-widest">Cambio</p>
          <p className="text-4xl font-black uppercase tracking-widest">Portiere!</p>
          <div className="grid grid-cols-2 gap-4 mt-8 px-6 max-w-md w-full">
            <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-center">
              <p className="text-[10px] uppercase opacity-80">Team A</p>
              <p className="text-lg font-bold truncate">{kpA?.user?.name || '—'}</p>
            </div>
            <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-center">
              <p className="text-[10px] uppercase opacity-80">Team B</p>
              <p className="text-lg font-bold truncate">{kpB?.user?.name || '—'}</p>
            </div>
          </div>
          <p className="mt-8 text-sm opacity-80">Tocca lo schermo per fermare</p>
        </button>
      )}
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Rotazione portiere</p>
            <p className="text-xs text-muted-foreground">
              Ciclo {GK_ROTATION_SECONDS / 60} min · giro {keeperIndex + 1}
            </p>
          </div>
          <div className={cn(
            'font-mono text-2xl font-bold tabular-nums',
            displayLeft <= 30 ? 'text-rose-600' : 'text-foreground',
          )}>
            {mm}:{ss}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-md border bg-card p-2 flex items-center gap-2">
            <Avatar className="h-7 w-7"><AvatarImage src={kpA?.user?.picture} /><AvatarFallback>{getInitials(kpA?.user?.name)}</AvatarFallback></Avatar>
            <div className="min-w-0">
              <p className="text-[10px] uppercase text-muted-foreground">Team A · in porta</p>
              <p className="truncate font-medium">{kpA?.user?.name || '—'}</p>
            </div>
          </div>
          <div className="rounded-md border bg-card p-2 flex items-center gap-2">
            <Avatar className="h-7 w-7"><AvatarImage src={kpB?.user?.picture} /><AvatarFallback>{getInitials(kpB?.user?.name)}</AvatarFallback></Avatar>
            <div className="min-w-0">
              <p className="text-[10px] uppercase text-muted-foreground">Team B · in porta</p>
              <p className="truncate font-medium">{kpB?.user?.name || '—'}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {running ? (
            <Button size="sm" variant="outline" className="flex-1" onClick={handlePause} disabled={disabled}>
              <Pause className="h-4 w-4 mr-1" /> Pausa
            </Button>
          ) : (
            <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={handleStart} disabled={disabled}>
              <Play className="h-4 w-4 mr-1" /> Avvia
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleSkip} disabled={disabled} title="Cambio ora">
            <Square className="h-4 w-4 mr-1" /> Cambio
          </Button>
          <Button size="icon" variant="ghost" onClick={handleReset} disabled={disabled} title="Reset"><RotateCcw className="h-4 w-4" /></Button>
        </div>
      </CardContent>
    </Card>
    </>
  );
};

const TeamPanel = ({ label, team, players, disabled, onQuickEvent, onMoreEvents, events }) => {
  // Conteggio gol per player (incluso own_goal? no, own_goal NON conta come marcatore)
  const goalsByPlayer = useMemo(() => {
    const m = {};
    for (const ev of events) {
      if ((ev.event_type === 'goal' || ev.event_type === 'penalty') && ev.player_id && ev.team === team) {
        m[ev.player_id] = (m[ev.player_id] || 0) + 1;
      }
    }
    return m;
  }, [events, team]);

  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">{label}</p>
          <Badge variant="outline" className="text-xs">{players.length} giocatori</Badge>
        </div>
        <div className="space-y-1.5">
          {players.length === 0 && (
            <p className="text-xs text-muted-foreground p-2 text-center">Nessun titolare confermato in questo team.</p>
          )}
          {players.map((p) => {
            const goals = goalsByPlayer[p.user_id] || 0;
            return (
              <div key={p.user_id} className="flex items-center gap-2 p-2 rounded-lg border bg-card">
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={p.user?.picture} />
                  <AvatarFallback>{getInitials(p.user?.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.user?.name}</p>
                  {goals > 0 && (
                    <p className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                      ⚽ {goals} {goals === 1 ? 'gol' : 'gol'}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={disabled}
                  onClick={() => onQuickEvent(p.user_id)}
                  className="border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                  title="Registra gol"
                >
                  <Goal className="h-4 w-4 mr-1 text-emerald-600" /> +1
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => onMoreEvents(p.user_id)}
                  title="Altri eventi"
                  className="h-9 w-9"
                >
                  <span className="text-lg leading-none">⋯</span>
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const EventsFeed = ({ events, participants, onDelete, isAdmin }) => {
  const partByUid = useMemo(() => {
    const m = {};
    participants.forEach((p) => { m[p.user_id] = p; });
    return m;
  }, [participants]);
  if (!events.length) return null;
  const recent = [...events].sort((a, b) => (b.minute ?? 0) - (a.minute ?? 0)).slice(0, 20);

  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-sm font-semibold mb-2">Eventi recenti</p>
        <div className="space-y-1.5">
          {recent.map((ev) => {
            const cfg = EVENT_TYPES[ev.event_type] || { label: ev.event_type, emoji: '•' };
            const p = partByUid[ev.player_id];
            const sp = ev.secondary_player_id ? partByUid[ev.secondary_player_id] : null;
            return (
              <div key={ev.event_id} className="flex items-center gap-2 text-sm p-2 rounded-lg border bg-card">
                <span className="text-lg">{cfg.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="truncate">
                    <span className="font-medium">{p?.user?.name || ev.player_id}</span>
                    <span className="text-muted-foreground"> · {cfg.label}</span>
                    {sp && <span className="text-muted-foreground"> (assist: {sp?.user?.name})</span>}
                  </p>
                </div>
                {ev.team && <Badge variant="outline" className="text-[10px]">{ev.team}</Badge>}
                {ev.minute != null && <span className="text-xs font-mono text-muted-foreground">{ev.minute}'</span>}
                {isAdmin && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => onDelete(ev.event_id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default MatchLivePage;
